// @ts-nocheck
import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Paper,
  makeStyles,
} from "@material-ui/core";
import { Title } from "react-admin";
import {
  buildPreviewHashLink,
  getCampaignPopupsConfig,
  putCampaignPopupsConfig,
  resolveCampaignPopup,
} from "./api";
import { canEditIntegrationConfigs } from "./permissions";
import {
  CampaignPopupDefault,
  CampaignPopupRule,
  CampaignPopupRuleRow,
  createDefaultPopup,
  createEmptyRule,
  findDuplicateIndex,
} from "./types";
import { getRoles } from "../auth-provider/ra-auth-jwt";

const useStyles = makeStyles((theme) => ({
  page: {
    direction: "rtl",
    padding: theme.spacing(2),
    fontFamily: "IranSans-Light",
  },
  header: {
    display: "flex",
    flexDirection: "row-reverse",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },
  title: {
    fontFamily: "IranSans-Bold",
    textAlign: "right",
    margin: 0,
  },
  pageHeading: {
    fontFamily: "IranSans-Bold",
    textAlign: "right",
    margin: "0 0 0.5rem 0",
    fontSize: "1.25rem",
  },
  bodyMuted: {
    fontFamily: "IranSans-Light",
    textAlign: "right",
    color: theme.palette.text.secondary,
    margin: "0 0 1rem 0",
    fontSize: "0.875rem",
  },
  subtitle: {
    fontFamily: "IranSans-Light",
    textAlign: "right",
    color: theme.palette.text.secondary,
    margin: 0,
    fontSize: "0.875rem",
  },
  sectionHeading: {
    fontFamily: "IranSans-Bold",
    textAlign: "right",
    margin: 0,
    fontSize: "0.875rem",
  },
  errorText: {
    fontFamily: "IranSans-Light",
    color: theme.palette.error.main,
    margin: 0,
    fontSize: "0.875rem",
  },
  captionLabel: {
    fontFamily: "IranSans-Light",
    textAlign: "right",
    color: theme.palette.text.secondary,
    margin: "0 0 0.5rem 0",
    fontSize: "0.75rem",
  },
  dialogTitle: {
    fontFamily: "IranSans-Bold",
    textAlign: "right",
    margin: 0,
    fontSize: "1.25rem",
  },
  table: {
    minWidth: 650,
  },
  actionsCell: {
    display: "flex",
    flexDirection: "row-reverse",
    flexWrap: "wrap",
    justifyContent: "center",
    gap: theme.spacing(0.5),
  },
  emptyCard: {
    textAlign: "right",
    padding: theme.spacing(3),
  },
  sectionTitle: {
    fontFamily: "IranSans-Bold",
    textAlign: "right",
    margin: "0 0 1rem 0",
    fontSize: "1.25rem",
  },
  alertError: {
    backgroundColor: "#fdecea",
    border: `1px solid ${theme.palette.error.main}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    textAlign: "right",
    fontFamily: "IranSans-Light",
  },
  resolveFields: {
    display: "flex",
    flexDirection: "row-reverse",
    flexWrap: "wrap",
    alignItems: "flex-start",
    gap: theme.spacing(1.5),
  },
  resolveResult: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: "#f5f5f5",
    borderRadius: theme.shape.borderRadius,
    overflow: "auto",
    maxHeight: 240,
    direction: "ltr",
    textAlign: "left",
    fontFamily: "monospace",
    fontSize: "0.8rem",
  },
  dialogSection: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  dialogActions: {
    flexDirection: "row-reverse",
    padding: theme.spacing(2),
    gap: theme.spacing(1),
  },
  ltrField: {
    direction: "ltr",
    textAlign: "left",
  },
  loadingBox: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(6),
  },
}));

function newClientId(): string {
  return `rule-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function toRows(rules: CampaignPopupRule[]): CampaignPopupRuleRow[] {
  return rules.map((r) => ({ ...r, _clientId: newClientId() }));
}

function truncate(text: string, max: number): string {
  if (!text) return "—";
  return text.length <= max ? text : `${text.slice(0, max)}…`;
}

function formatUpdatedAt(value?: string): string {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("fa-IR");
  } catch {
    return value;
  }
}

function validateRule(rule: CampaignPopupRule): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!rule.campaign.trim()) {
    errors.campaign = "کمپین الزامی است";
  }
  if (rule.enabled) {
    if (!rule.title.trim()) errors.title = "عنوان الزامی است";
    if (!rule.body.trim()) errors.body = "متن الزامی است";
    if (!rule.ctaText.trim()) errors.ctaText = "برچسب دکمه الزامی است";
    if (!rule.dismissText.trim()) errors.dismissText = "برچسب بستن الزامی است";
  }
  if (rule.title.length > 120) errors.title = "حداکثر ۱۲۰ کاراکتر";
  if (rule.body.length > 2000) errors.body = "حداکثر ۲۰۰۰ کاراکتر";
  if (rule.ctaText.length > 80) errors.ctaText = "حداکثر ۸۰ کاراکتر";
  if (rule.dismissText.length > 40) errors.dismissText = "حداکثر ۴۰ کاراکتر";
  return errors;
}

function validateDefaultPopup(popup: CampaignPopupDefault): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!popup.enabled) return errors;
  if (!popup.title.trim()) errors.title = "عنوان الزامی است";
  if (!popup.body.trim()) errors.body = "متن الزامی است";
  if (!popup.ctaText.trim()) errors.ctaText = "برچسب دکمه الزامی است";
  if (popup.title.length > 120) errors.title = "حداکثر ۱۲۰ کاراکتر";
  if (popup.body.length > 2000) errors.body = "حداکثر ۲۰۰۰ کاراکتر";
  if (popup.ctaText.length > 80) errors.ctaText = "حداکثر ۸۰ کاراکتر";
  if (popup.dismissText.length > 40) errors.dismissText = "حداکثر ۴۰ کاراکتر";
  return errors;
}

export function NewBetterMeCampaignPopups() {
  const classes = useStyles();
  const roles = getRoles() || [];
  const allowed = canEditIntegrationConfigs(roles);

  const [rows, setRows] = useState<CampaignPopupRuleRow[]>([]);
  const [popupsEnabled, setPopupsEnabled] = useState(true);
  const [defaultPopup, setDefaultPopup] = useState<CampaignPopupDefault>(
    createDefaultPopup()
  );
  const [defaultErrors, setDefaultErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: "", ok: true });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [form, setForm] = useState<CampaignPopupRule>(createEmptyRule());
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [duplicateWarn, setDuplicateWarn] = useState("");

  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);

  const [resolveCampaign, setResolveCampaign] = useState("");
  const [resolveSource, setResolveSource] = useState("");
  const [resolveMedium, setResolveMedium] = useState("");
  const [resolveLoading, setResolveLoading] = useState(false);
  const [resolveResult, setResolveResult] = useState<string | null>(null);

  const showSnack = (message: string, ok: boolean) => {
    setSnack({ open: true, message, ok });
  };

  const loadRules = useCallback(async () => {
    setLoading(true);
    try {
      const config = await getCampaignPopupsConfig();
      setPopupsEnabled(config.enabled);
      setRows(toRows(config.rules));
      setDefaultPopup(config.defaultPopup);
      setDefaultErrors({});
    } catch (e: any) {
      showSnack(e?.message || "خطا در بارگذاری", false);
      setPopupsEnabled(true);
      setRows([]);
      setDefaultPopup(createDefaultPopup());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (allowed) loadRules();
    else setLoading(false);
  }, [allowed, loadRules]);

  const persistConfig = async (
    nextRules: CampaignPopupRule[],
    nextDefault: CampaignPopupDefault = defaultPopup,
    nextEnabled: boolean = popupsEnabled
  ) => {
    setSaving(true);
    try {
      const saved = await putCampaignPopupsConfig({
        enabled: nextEnabled,
        rules: nextRules,
        defaultPopup: nextDefault,
      });
      setPopupsEnabled(saved.enabled);
      setRows(toRows(saved.rules));
      setDefaultPopup(saved.defaultPopup);
      showSnack("ذخیره شد", true);
      return true;
    } catch (e: any) {
      const msg =
        e?.response?.data?.err?.message ||
        e?.message ||
        "خطا در ذخیره";
      showSnack(msg, false);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDefaultPopup = async () => {
    const errors = validateDefaultPopup(defaultPopup);
    setDefaultErrors(errors);
    if (Object.keys(errors).length) return;
    await persistConfig(rulesFromRows(rows), defaultPopup);
  };

  const rulesFromRows = (list: CampaignPopupRuleRow[]): CampaignPopupRule[] =>
    list.map(({ _clientId, ...r }) => r);

  const openCreate = () => {
    setEditIndex(null);
    setForm(createEmptyRule());
    setFormErrors({});
    setDuplicateWarn("");
    setDialogOpen(true);
  };

  const openEdit = (index: number) => {
    const { _clientId, ...rule } = rows[index];
    setEditIndex(index);
    setForm({ ...rule });
    setFormErrors({});
    setDuplicateWarn("");
    setDialogOpen(true);
  };

  const openDuplicate = (index: number) => {
    const { _clientId, ...rule } = rows[index];
    setEditIndex(null);
    setForm({
      ...rule,
      campaign: `${rule.campaign}-copy`,
    });
    setFormErrors({});
    setDuplicateWarn("");
    setDialogOpen(true);
  };

  const handleToggleEnabled = async (index: number) => {
    const next = rows.map((r, i) =>
      i === index ? { ...r, enabled: !r.enabled } : r
    );
    setRows(next);
    await persistConfig(rulesFromRows(next));
  };

  const handleDeleteConfirm = async () => {
    if (deleteIndex === null) return;
    const next = rows.filter((_, i) => i !== deleteIndex);
    const ok = await persistConfig(rulesFromRows(next));
    if (ok) setDeleteIndex(null);
  };

  const handleFormSave = async () => {
    const errors = validateRule(form);
    setFormErrors(errors);
    if (Object.keys(errors).length) return;

    const baseRules = rulesFromRows(rows);
    const dupIdx = findDuplicateIndex(
      baseRules,
      form,
      editIndex === null ? undefined : editIndex
    );
    if (dupIdx >= 0) {
      setDuplicateWarn(
        "قانونی با همین کمپین و UTM Source/Medium وجود دارد. قبل از ذخیره آن را ویرایش یا حذف کنید."
      );
      return;
    }
    setDuplicateWarn("");

    const nextRule: CampaignPopupRule = {
      ...form,
      campaign: form.campaign.trim(),
      utm_source: form.utm_source?.trim() || "",
      utm_medium: form.utm_medium?.trim() || "",
    };

    let nextRows: CampaignPopupRuleRow[];
    if (editIndex === null) {
      nextRows = [...rows, { ...nextRule, _clientId: newClientId() }];
    } else {
      nextRows = rows.map((r, i) =>
        i === editIndex ? { ...nextRule, _clientId: r._clientId } : r
      );
    }

    const ok = await persistConfig(rulesFromRows(nextRows));
    if (ok) {
      setDialogOpen(false);
      setEditIndex(null);
    }
  };

  const copyPreviewLink = async (rule: CampaignPopupRule) => {
    const link = buildPreviewHashLink(rule);
    try {
      await navigator.clipboard.writeText(link);
      showSnack("لینک پیش‌نمایش کپی شد", true);
    } catch {
      showSnack(link, true);
    }
  };

  const runResolveTest = async () => {
    if (!resolveCampaign.trim()) {
      setResolveResult("No popup");
      return;
    }
    setResolveLoading(true);
    setResolveResult(null);
    try {
      const popup = await resolveCampaignPopup({
        utm_campaign: resolveCampaign.trim(),
        utm_source: resolveSource.trim() || undefined,
        utm_medium: resolveMedium.trim() || undefined,
      });
      if (!popup) {
        setResolveResult("No popup");
      } else {
        setResolveResult(JSON.stringify(popup, null, 2));
      }
    } catch (e: any) {
      setResolveResult(e?.message || "خطا در resolve");
    } finally {
      setResolveLoading(false);
    }
  };

  const emptyState = !loading && rows.length === 0;

  if (!allowed) {
    return (
      <div className={classes.page} dir="rtl">
        <Title title="پاپ‌آپ NewBetterMe" />
        <Card>
          <CardContent className={classes.emptyCard}>
            <p className={classes.title}>
              شما دسترسی ویرایش این تنظیمات را ندارید.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleTogglePopupsEnabled = async () => {
    const next = !popupsEnabled;
    setPopupsEnabled(next);
    const ok = await persistConfig(rulesFromRows(rows), defaultPopup, next);
    if (!ok) setPopupsEnabled(popupsEnabled);
  };

  const defaultFieldsDisabled = loading || saving || !popupsEnabled || !defaultPopup.enabled;

  return (
    <div className={classes.page} dir="rtl">
      <Title title="پاپ‌آپ NewBetterMe" />

      <Card style={{ marginBottom: "1.5rem" }}>
        <CardContent>
          <div className={classes.header}>
            <FormControlLabel
              control={
                <Switch
                  checked={popupsEnabled}
                  onChange={handleTogglePopupsEnabled}
                  disabled={loading || saving}
                  color="primary"
                />
              }
              label="نمایش پاپ‌آپ در لندینگ NewBetterMe"
            />
          </div>
          <p className={classes.bodyMuted}>
            با غیرفعال کردن، هیچ پاپ‌آپی (پیش‌فرض و کمپین) در لندینگ نمایش داده
            نمی‌شود.
          </p>
        </CardContent>
      </Card>

      <Card style={{ marginBottom: "1.5rem" }}>
        <CardContent>
          <div className={classes.header}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={defaultPopup.enabled}
                  onChange={(e) =>
                    setDefaultPopup({
                      ...defaultPopup,
                      enabled: e.target.checked,
                    })
                  }
                  color="primary"
                  disabled={loading || saving || !popupsEnabled}
                />
              }
              label="پاپ‌آپ پیش‌فرض فعال"
            />
          </div>
          <p className={classes.bodyMuted}>
            وقتی utm_campaign با هیچ قانونی مطابقت نداشته باشد یا کاربر
            utm_campaign نداشته باشد، این متن نمایش داده می‌شود.
          </p>
          <Grid container spacing={2} direction="row-reverse">
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="عنوان *"
                variant="outlined"
                size="small"
                value={defaultPopup.title}
                onChange={(e) =>
                  setDefaultPopup({ ...defaultPopup, title: e.target.value })
                }
                error={!!defaultErrors.title}
                helperText={defaultErrors.title}
                inputProps={{ maxLength: 120, dir: defaultPopup.dir }}
                disabled={defaultFieldsDisabled}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="متن *"
                variant="outlined"
                size="small"
                value={defaultPopup.body}
                onChange={(e) =>
                  setDefaultPopup({ ...defaultPopup, body: e.target.value })
                }
                multiline
                rows={5}
                error={!!defaultErrors.body}
                helperText={defaultErrors.body}
                inputProps={{ maxLength: 2000, dir: defaultPopup.dir }}
                disabled={defaultFieldsDisabled}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="برچسب دکمه *"
                variant="outlined"
                size="small"
                value={defaultPopup.ctaText}
                onChange={(e) =>
                  setDefaultPopup({ ...defaultPopup, ctaText: e.target.value })
                }
                inputProps={{ maxLength: 80 }}
                error={!!defaultErrors.ctaText}
                helperText={defaultErrors.ctaText}
                disabled={defaultFieldsDisabled}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="برچسب بستن"
                variant="outlined"
                size="small"
                value={defaultPopup.dismissText}
                onChange={(e) =>
                  setDefaultPopup({
                    ...defaultPopup,
                    dismissText: e.target.value,
                  })
                }
                helperText={
                  defaultErrors.dismissText ||
                  "خالی = بدون دکمه بستن در لندینگ"
                }
                inputProps={{ maxLength: 40 }}
                error={!!defaultErrors.dismissText}
                disabled={defaultFieldsDisabled}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>جهت متن</InputLabel>
                <Select
                  value={defaultPopup.dir}
                  onChange={(e) =>
                    setDefaultPopup({
                      ...defaultPopup,
                      dir: e.target.value as "rtl" | "ltr",
                    })
                  }
                  label="جهت متن"
                  disabled={defaultFieldsDisabled}
                >
                  <MenuItem value="rtl">راست به چپ</MenuItem>
                  <MenuItem value="ltr">چپ به راست</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={defaultPopup.showOncePerSession}
                    onChange={(e) =>
                      setDefaultPopup({
                        ...defaultPopup,
                        showOncePerSession: e.target.checked,
                      })
                    }
                    color="primary"
                    disabled={defaultFieldsDisabled}
                  />
                }
                label="فقط یک‌بار در هر نشست"
              />
            </Grid>
          </Grid>
          <Box mt={2} display="flex" justifyContent="flex-end">
            <Button
              color="primary"
              variant="contained"
              onClick={handleSaveDefaultPopup}
              disabled={loading || saving || !popupsEnabled}
            >
              {saving ? <CircularProgress size={22} /> : "ذخیره پیش‌فرض"}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <div className={classes.header}>
            <Button
              color="primary"
              variant="contained"
              onClick={openCreate}
              disabled={loading || saving}
            >
              افزودن قانون
            </Button>
            <div>
              <h2 className={classes.pageHeading}>
                قوانین کمپین (utm_campaign)
              </h2>
              <p className={classes.bodyMuted}>
                متن پاپ‌آپ را برای هر utm_campaign (و در صورت نیاز utm_source /
                utm_medium) تنظیم کنید؛ بدون دیپلوی فرانت اعمال می‌شود.
              </p>
            </div>
          </div>

          {loading ? (
            <div className={classes.loadingBox}>
              <CircularProgress color="primary" />
            </div>
          ) : emptyState ? (
            <div className={classes.emptyCard}>
              <p className={classes.title} style={{ marginBottom: "0.5rem" }}>
                هنوز قانونی ثبت نشده است
              </p>
              <p className={classes.bodyMuted}>
                با دکمه «افزودن قانون» اولین پاپ‌آپ کمپین را تعریف کنید.
              </p>
              <Box mt={2} display="flex" justifyContent="flex-end">
                <Button
                  color="primary"
                  variant="outlined"
                  onClick={openCreate}
                >
                  افزودن اولین قانون
                </Button>
              </Box>
            </div>
          ) : (
            <TableContainer component={Paper} elevation={1}>
              <Table size="small" className={classes.table}>
                <TableHead>
                  <TableRow>
                    <TableCell align="center">فعال</TableCell>
                    <TableCell align="center">کمپین</TableCell>
                    <TableCell align="center">UTM Source</TableCell>
                    <TableCell align="center">UTM Medium</TableCell>
                    <TableCell align="center">عنوان</TableCell>
                    <TableCell align="center">به‌روزرسانی</TableCell>
                    <TableCell align="center">عملیات</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row, index) => (
                    <TableRow key={row._clientId} hover>
                      <TableCell align="center">
                        <Switch
                          checked={row.enabled}
                          onChange={() => handleToggleEnabled(index)}
                          disabled={saving}
                          color="primary"
                        />
                      </TableCell>
                      <TableCell align="center" className={classes.ltrField}>
                        {row.campaign || "—"}
                      </TableCell>
                      <TableCell align="center" className={classes.ltrField}>
                        {row.utm_source || "—"}
                      </TableCell>
                      <TableCell align="center" className={classes.ltrField}>
                        {row.utm_medium || "—"}
                      </TableCell>
                      <TableCell align="center">
                        {truncate(row.title, 40)}
                      </TableCell>
                      <TableCell align="center">
                        {formatUpdatedAt(row.updatedAt)}
                      </TableCell>
                      <TableCell align="center">
                        <div className={classes.actionsCell}>
                          <Button
                            size="small"
                            color="primary"
                            variant="text"
                            onClick={() => openEdit(index)}
                            disabled={saving}
                          >
                            ویرایش
                          </Button>
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => openDuplicate(index)}
                            disabled={saving}
                          >
                            کپی
                          </Button>
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => copyPreviewLink(row)}
                            disabled={saving || !row.campaign}
                          >
                            پیش‌نمایش
                          </Button>
                          <Button
                            size="small"
                            color="secondary"
                            variant="text"
                            onClick={() => setDeleteIndex(index)}
                            disabled={saving}
                          >
                            حذف
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <div className="search-panel" style={{ marginTop: "1.5rem" }}>
        <h2 className={classes.sectionTitle}>تست resolve</h2>
        <p className={classes.bodyMuted}>
          پارامترهای UTM را وارد کنید تا ببینید کدام قانون برای لندینگ انتخاب
          می‌شود.
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            runResolveTest();
          }}
        >
          <div className={classes.resolveFields}>
            <TextField
              label="utm_campaign"
              placeholder="مثال: tabriz"
              value={resolveCampaign}
              onChange={(e) => setResolveCampaign(e.target.value)}
              variant="outlined"
              size="small"
              className={classes.ltrField}
              InputProps={{ className: classes.ltrField }}
            />
            <TextField
              label="utm_source"
              value={resolveSource}
              onChange={(e) => setResolveSource(e.target.value)}
              variant="outlined"
              size="small"
              className={classes.ltrField}
              InputProps={{ className: classes.ltrField }}
            />
            <TextField
              label="utm_medium"
              value={resolveMedium}
              onChange={(e) => setResolveMedium(e.target.value)}
              variant="outlined"
              size="small"
              className={classes.ltrField}
              InputProps={{ className: classes.ltrField }}
            />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={resolveLoading}
            >
              {resolveLoading ? (
                <CircularProgress size={22} color="inherit" />
              ) : (
                "اجرای resolve"
              )}
            </Button>
          </div>
        </form>
        {resolveResult !== null && (
          <Box>
            <p className={classes.captionLabel}>نتیجه:</p>
            <pre className={classes.resolveResult}>{resolveResult}</pre>
          </Box>
        )}
      </div>

      <Dialog
        open={dialogOpen}
        onClose={() => !saving && setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        dir="rtl"
      >
        <DialogTitle disableTypography>
          <p className={classes.dialogTitle}>
            {editIndex === null ? "افزودن قانون پاپ‌آپ" : "ویرایش قانون پاپ‌آپ"}
          </p>
        </DialogTitle>
        <DialogContent dividers>
          {duplicateWarn && (
            <Box className={classes.alertError}>
              <p className={classes.errorText}>{duplicateWarn}</p>
            </Box>
          )}

          <FormControlLabel
            control={
              <Checkbox
                checked={form.enabled}
                onChange={(e) =>
                  setForm({ ...form, enabled: e.target.checked })
                }
                color="primary"
              />
            }
            label="فعال"
          />

          <p className={`${classes.sectionHeading} ${classes.dialogSection}`}>
            تطبیق UTM
          </p>
          <Grid container spacing={2} direction="row-reverse">
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="کمپین *"
                variant="outlined"
                size="small"
                value={form.campaign}
                onChange={(e) =>
                  setForm({ ...form, campaign: e.target.value })
                }
                error={!!formErrors.campaign}
                helperText={
                  formErrors.campaign ||
                  "مطابق utm_campaign در لینک تبلیغ (case-insensitive). چند کمپین: با ویرگول، مثلاً tabriz, diabetic"
                }
                InputProps={{ className: classes.ltrField }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="UTM Source"
                variant="outlined"
                size="small"
                value={form.utm_source || ""}
                onChange={(e) =>
                  setForm({ ...form, utm_source: e.target.value })
                }
                InputProps={{ className: classes.ltrField }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="UTM Medium"
                variant="outlined"
                size="small"
                value={form.utm_medium || ""}
                onChange={(e) =>
                  setForm({ ...form, utm_medium: e.target.value })
                }
                InputProps={{ className: classes.ltrField }}
              />
            </Grid>
          </Grid>

          <Divider className={classes.dialogSection} />

          <p className={`${classes.sectionHeading} ${classes.dialogSection}`}>
            متن پاپ‌آپ
          </p>
          <Grid container spacing={2} direction="row-reverse">
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="عنوان *"
                variant="outlined"
                size="small"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                error={!!formErrors.title}
                helperText={formErrors.title}
                inputProps={{ maxLength: 120, dir: form.dir }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="متن *"
                variant="outlined"
                size="small"
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                multiline
                rows={5}
                error={!!formErrors.body}
                helperText={formErrors.body}
                inputProps={{ maxLength: 2000, dir: form.dir }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="برچسب دکمه *"
                variant="outlined"
                size="small"
                value={form.ctaText}
                onChange={(e) =>
                  setForm({ ...form, ctaText: e.target.value })
                }
                inputProps={{ maxLength: 80 }}
                error={!!formErrors.ctaText}
                helperText={formErrors.ctaText}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="برچسب بستن *"
                variant="outlined"
                size="small"
                value={form.dismissText}
                onChange={(e) =>
                  setForm({ ...form, dismissText: e.target.value })
                }
                inputProps={{ maxLength: 40 }}
                error={!!formErrors.dismissText}
                helperText={formErrors.dismissText}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>جهت متن</InputLabel>
                <Select
                  value={form.dir}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      dir: e.target.value as "rtl" | "ltr",
                    })
                  }
                  label="جهت متن"
                >
                  <MenuItem value="rtl">راست به چپ</MenuItem>
                  <MenuItem value="ltr">چپ به راست</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Divider className={classes.dialogSection} />

          <FormControlLabel
            control={
              <Checkbox
                checked={form.showOncePerSession}
                onChange={(e) =>
                  setForm({ ...form, showOncePerSession: e.target.checked })
                }
                color="primary"
              />
            }
            label="فقط یک‌بار در هر نشست نمایش داده شود"
          />
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button
            onClick={handleFormSave}
            color="primary"
            variant="contained"
            disabled={saving}
          >
            {saving ? <CircularProgress size={22} /> : "ذخیره"}
          </Button>
          <Button
            onClick={() => setDialogOpen(false)}
            disabled={saving}
            variant="outlined"
          >
            انصراف
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteIndex !== null}
        onClose={() => setDeleteIndex(null)}
        dir="rtl"
      >
        <DialogTitle disableTypography>
          <p className={classes.dialogTitle}>حذف قانون</p>
        </DialogTitle>
        <DialogContent>
          <p className={classes.subtitle}>
            این قانون حذف شود؟ این عمل قابل بازگشت نیست مگر با بازیابی از
            پشتیبان.
          </p>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button
            color="secondary"
            variant="contained"
            onClick={handleDeleteConfirm}
            disabled={saving}
          >
            حذف
          </Button>
          <Button onClick={() => setDeleteIndex(null)} variant="outlined">
            انصراف
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack({ ...snack, open: false })}
        message={snack.message}
        className={snack.ok ? "snack-successful" : "snack-error"}
      />
    </div>
  );
}
